import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "../session/SessionContext";
import { EmptyState, ErrorState, PermissionDeniedState } from "../components/states";
import { MyGroupsSummary } from "../components/groups/MyGroupsSummary";
import { LayoutSwitcher, SectionLayoutProvider } from "../components/layout/SectionLayout";
import { GroupSortControl } from "../components/groups/GroupSortControl";
import { isShownGroup, type GroupSortKey } from "../components/groups/groupSort";
import { HideEmptyGroupsToggle } from "../components/groups/HideEmptyGroupsToggle";
import { isCurrentPeriod, useSelectedPeriod } from "../hooks/useSelectedPeriod";
import { fetchMyGroups, fetchMyGroupsDailySpend, type MyGroupsDailySpendResponse, type MyGroupsResponse } from "../api/organisationReporting";
import { unchangedOr, usePolling } from "../hooks/usePolling";
import { GroupsLoading } from "../components/groups/GroupsLoading";

const OthersConsumptionPermission = "consumption.read.others";

type PageState =
  | { kind: "loading" }
  | { kind: "loaded"; response: MyGroupsResponse }
  | { kind: "denied" }
  | { kind: "error"; message: string };

/**
 * The "My Groups" screen: `consumption.read.others` permits aggregated usage for the groups they belong to.
 * Group spend remains separately permission-gated and never exposes a named individual's figures.
 */
export function MyGroupsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { snapshot } = useSession();
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [dailySpend, setDailySpend] = useState<MyGroupsDailySpendResponse | null>(null);
  const [periodStart] = useSelectedPeriod();
  const [chosenSortKey, setSortKey] = useState<GroupSortKey>("hierarchy");
  // Projected spend exists only for the current month, so a past month falls back to the server's order.
  const projectedAvailable = isCurrentPeriod(periodStart);
  const sortKey: GroupSortKey = chosenSortKey === "projected" && !projectedAvailable ? "hierarchy" : chosenSortKey;
  const [hideEmptyGroups, setHideEmptyGroups] = useState(false);

  const hasPermission = snapshot.status === "signed-in" && snapshot.permissions.includes(OthersConsumptionPermission);

  const load = useCallback(
    // A quiet load refreshes the figures in place, without the loading layout, and keeps them if it fails.
    async (signal?: AbortSignal, quiet = false) => {
      if (!quiet) {
        setState({ kind: "loading" });
      }
      // Fetched together and committed together, so the charts are never drawn against the previous
      // period's daily series and then redrawn.
      const [result, dailySpendResult] = await Promise.all([
        fetchMyGroups(periodStart, signal),
        fetchMyGroupsDailySpend(periodStart, signal),
      ]);
      if (result.kind === "aborted" || dailySpendResult.kind === "aborted") {
        return;
      }
      if (quiet && result.kind !== "ok") {
        return;
      }

      switch (result.kind) {
        case "denied":
          setState({ kind: "denied" });
          return;
        case "error":
          setState({ kind: "error", message: result.message });
          return;
        case "ok": {
          const next: PageState = { kind: "loaded", response: result.response };
          setState((previous) => unchangedOr(previous, next));
        }
      }

      // The daily-spend series is supplementary (total usage over time): left null on failure rather
      // than surfacing a second error state.
      setDailySpend((previous) => unchangedOr(previous, dailySpendResult.kind === "ok" ? dailySpendResult.response : null));
    },
    [periodStart],
  );

  useEffect(() => {
    const controller = new AbortController();
    if (snapshot.status === "signed-in") {
      if (hasPermission) void load(controller.signal);
    }
    return () => controller.abort();
  }, [hasPermission, load, snapshot.status]);

  usePolling(() => load(undefined, true), hasPermission && state.kind === "loaded");

  if (snapshot.status !== "signed-in") {
    return (
      <div>
        <PermissionDeniedState title={t("myGroups.denied.title")} description={t("myGroups.denied.description")} />
      </div>
    );
  }

  return (
    <SectionLayoutProvider>
      <div>
        {hasPermission ? (
          <LayoutSwitcher
            leading={
              <>
                <GroupSortControl value={sortKey} onChange={setSortKey} projectedAvailable={projectedAvailable} />
                <HideEmptyGroupsToggle pressed={hideEmptyGroups} onChange={setHideEmptyGroups} />
              </>
            }
          />
        ) : null}
        <MyGroupsPageBody
          state={state}
          hasPermission={hasPermission}
          dailySpend={dailySpend}
          sortKey={sortKey}
          hideEmptyGroups={hideEmptyGroups}
          onRetry={() => void load()}
        />
      </div>
    </SectionLayoutProvider>
  );
}

function MyGroupsPageBody({
  state,
  hasPermission,
  dailySpend,
  sortKey,
  hideEmptyGroups,
  onRetry,
}: {
  state: PageState;
  hasPermission: boolean;
  dailySpend: MyGroupsDailySpendResponse | null;
  sortKey: GroupSortKey;
  hideEmptyGroups: boolean;
  onRetry: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return <>
    {!hasPermission ? <PermissionDeniedState title={t("myGroups.denied.title")} description={t("myGroups.denied.description")} /> : null}
    {hasPermission && state.kind === "loading" ? <GroupsLoading /> : null}
    {hasPermission && state.kind === "denied" ? <PermissionDeniedState title={t("myGroups.denied.title")} description={t("myGroups.denied.description")} /> : null}
    {hasPermission && state.kind === "error" ? <GroupsLoading error={<ErrorState title={t("myGroups.errorTitle")} description={state.message} onRetry={onRetry} />} /> : null}
    {hasPermission && state.kind === "loaded" ? state.response.groups.some((group) => isShownGroup(group, hideEmptyGroups)) ? <MyGroupsSummary response={state.response} dailySpend={dailySpend} sortKey={sortKey} hideEmptyGroups={hideEmptyGroups} /> :<EmptyState title={t("myGroups.emptyTitle")} description={t("myGroups.emptyDescription")} /> : null}
  </>;
}
